import Box from '@risk-smart/themed-cloudscape-components/box';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartPopover from '../../../../../node_modules/@risk-smart/themed-cloudscape-components/internal/components/chart-popover';
import { WidgetLoading } from '../widget-loading';
import { HeatmapCell } from './HeatmapCell';
import styles from './style.module.scss';
import type { CellClickData, HeatmapCellData, TooltipData } from './types';

type Props = {
  loading?: boolean;
  /** [y-axis][x-axis] */
  data: HeatmapCellData[][];
  onCellClick?: (data: CellClickData) => void;
  controlType: Risk_Assessment_Result_Control_Type_Enum;
};

export const HeatmapWidgetCloudscape: FC<Props> = ({
  data,
  loading,
  onCellClick,
  controlType,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix:
      controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
        ? 'dashboard.widgets.controlledRiskHeatMap'
        : 'dashboard.widgets.uncontrolledRiskHeatMap',
  });
  const [tooltipData, setTooltipData] = useState<TooltipData>();
  const { getLabelByIndex: getImpactLabelByIndex } = useRating('impact');
  const { getLabelByIndex: getLikelihoodLabelByIndex } =
    useRating('likelihood');

  const renderPopoverContent = useCallback(
    (tooltipData: TooltipData) => {
      const { x, y, data } = tooltipData;

      return (
        <div>
          <Box variant={'strong'}>
            {t('popover.likelihood')}
            {':'}
          </Box>
          <Box>{getLikelihoodLabelByIndex(y)}</Box>
          <Box variant={'strong'}>
            {t('popover.impact')}
            {':'}
          </Box>
          <Box>{getImpactLabelByIndex(x)}</Box>
          <Box variant={'strong'}>
            {t('popover.recordCount')}
            {':'}
          </Box>
          <Box>{data.value}</Box>
          {data.label && (
            <>
              <Box variant={'strong'}>
                {t('popover.label')}
                {':'}
              </Box>
              <Box>{data.label}</Box>
            </>
          )}
        </div>
      );
    },
    [getImpactLabelByIndex, getLikelihoodLabelByIndex, t]
  );

  if (loading) {
    return <WidgetLoading />;
  }
  const rows: ReactNode[] = [];
  for (let row = data.length - 1; row >= 0; row--) {
    const rowData = data[row];
    const rowCells: ReactNode[] = [];

    for (let column = 0; column < rowData.length; column++) {
      const cellData = rowData[column];
      rowCells.push(
        <HeatmapCell
          onClick={(data) =>
            onCellClick?.({
              data,
              y: row,
              x: column,
            })
          }
          key={`${row}-${column}`}
          data={cellData}
          onMouseOver={(ref, data) =>
            setTooltipData({
              ref,
              data,
              y: row,
              x: column,
            })
          }
        />
      );
    }
    rows.push(
      <div key={row} className={styles.row}>
        {rowCells}
      </div>
    );
  }

  return (
    <div className={styles.gridContainer}>
      <div className={styles.yAxisTitle}>
        <Box variant={'h5'} margin={{ left: 'xs' }}>
          {t('yAxisTitle')}
        </Box>
      </div>
      <div
        className={styles.table}
        onMouseLeave={() => setTooltipData(undefined)}
      >
        {rows}
      </div>
      <div className={styles.xAxisTitle}>
        <Box variant={'h5'} margin={{ top: 'xxs' }}>
          {t('xAxisTitle')}
        </Box>
      </div>
      {tooltipData && (
        <ChartPopover
          title={<></>}
          trackRef={tooltipData.ref}
          trackKey={1}
          onDismiss={() => setTooltipData(undefined)}
          container={null}
          size={'small'}
          onMouseLeave={() => setTooltipData(undefined)}
        >
          {renderPopoverContent(tooltipData)}
        </ChartPopover>
      )}
    </div>
  );
};
